import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { api, errText } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { toast } from 'sonner';
import { ShieldCheck, Stethoscope, ClipboardList, Loader2, KeyRound, ArrowLeft, Crown, CreditCard } from 'lucide-react';
import { useDemoLaunch } from '@/components/DemoAccessGate';

// The backend's lapsed-subscription 403 (auth.LAPSED_DETAIL) — matched on its text.
const isLapsed = (err) => err?.response?.status === 403 && /subscription has lapsed/i.test(err?.response?.data?.detail || '');

const roleHome = (role) => (role === 'doctor' ? '/doctor' : role === 'nurse' ? '/nurse' : role === 'owner' ? '/owner' : '/admin');

export default function Login() {
  const { login, verifyMfa } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const { launch, dialog, busyRole: demoBusy, demoMode } = useDemoLaunch();
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [backupMode, setBackupMode] = useState(false);
  const [lapsed, setLapsed] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const result = await login(email, password);
      if (result.mfaRequired) {
        setMfaToken(result.mfaToken);
        setMfaCode('');
        setBackupMode(false);
        return;
      }
      const dest = location.state?.from || roleHome(result.user.role);
      navigate(dest, { replace: true });
    } catch (err) {
      if (isLapsed(err)) { setLapsed(true); return; }
      toast.error(errText(err, 'Sign in failed. Check your email and password.'));
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e?.preventDefault?.();
    if (!mfaCode.trim()) { toast.error('Enter your verification code'); return; }
    setBusy(true);
    try {
      const result = await verifyMfa(mfaToken, mfaCode.trim());
      if (result.backupCodeUsed) {
        toast.warning(`Backup code used — ${result.backupCodesRemaining} remaining. Consider re-enrolling your authenticator.`);
      }
      const dest = location.state?.from || roleHome(result.user.role);
      navigate(dest, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (isLapsed(err)) {
        setMfaToken('');
        setLapsed(true);
      } else if (status === 401 && (err?.response?.data?.detail || '').includes('MFA session')) {
        toast.error('Your sign-in expired. Please enter your password again.');
        setMfaToken('');
      } else {
        toast.error(errText(err, 'Invalid verification code'));
      }
      setMfaCode('');
    } finally {
      setBusy(false);
    }
  };

  const tryDemo = (role) => launch(role, () => navigate(roleHome(role), { replace: true }));

  return (
    <div className="min-h-screen hero-wash">
      {dialog}
      <div className="noise min-h-screen flex flex-col">
        <header className="h-14 flex items-center px-4 lg:px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="login-logo">
            <div className="h-7 w-7 rounded-lg bg-primary text-primary-foreground grid place-items-center font-bold">M</div>
            <span className="font-semibold tracking-tight">DoctorPaperwork</span>
          </Link>
        </header>
        <div className="flex-1 grid place-items-center px-4 py-10">
          <div className="w-full max-w-md space-y-4">
            {lapsed ? (
            <Card className="shadow-lg shadow-black/5" data-testid="login-lapsed-card">
              <CardHeader>
                <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-700 grid place-items-center mb-2">
                  <CreditCard className="h-5 w-5" />
                </div>
                <CardTitle className="text-xl">Your practice's subscription has lapsed</CardTitle>
                <CardDescription>
                  Don't worry — nothing has been lost. Your cases and documents are safe and will be right where you left them.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-muted-foreground">
                <p>To restore access, a practice admin can sign in and update billing. If that's not you, please let your practice admin know.</p>
                <Button variant="outline" className="w-full" onClick={() => { setLapsed(false); setPassword(''); }} data-testid="login-lapsed-back">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back to sign in
                </Button>
              </CardContent>
            </Card>
            ) : mfaToken ? (
            <Card className="shadow-lg shadow-black/5" data-testid="login-mfa-card">
              <CardHeader>
                <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary grid place-items-center mb-2">
                  <KeyRound className="h-5 w-5" />
                </div>
                <CardTitle className="text-xl">Two-factor verification</CardTitle>
                <CardDescription>
                  {backupMode ? 'Enter one of your backup codes (e.g. A1B2-C3D4).' : 'Enter the 6-digit code from your authenticator app.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={submitMfa} className="space-y-4">
                  {backupMode ? (
                    <Input
                      autoFocus
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.toUpperCase())}
                      placeholder="XXXX-XXXX"
                      maxLength={12}
                      className="font-mono tracking-widest text-center"
                      data-testid="login-mfa-backup-input"
                    />
                  ) : (
                    <div className="flex justify-center">
                      <InputOTP maxLength={6} value={mfaCode} onChange={setMfaCode} data-testid="login-mfa-otp-input">
                        <InputOTPGroup>
                          {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                  )}
                  <Button type="submit" className="w-full" disabled={busy || mfaCode.trim().length < (backupMode ? 8 : 6)} data-testid="login-mfa-submit">
                    {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Verify & sign in
                  </Button>
                </form>
                <div className="mt-4 flex items-center justify-between text-xs">
                  <button type="button" className="text-primary hover:underline" onClick={() => { setBackupMode(!backupMode); setMfaCode(''); }} data-testid="login-mfa-toggle-backup">
                    {backupMode ? 'Use authenticator code' : 'Use a backup code instead'}
                  </button>
                  <button type="button" className="text-muted-foreground hover:underline inline-flex items-center gap-1" onClick={() => { setMfaToken(''); setMfaCode(''); }} data-testid="login-mfa-back">
                    <ArrowLeft className="h-3 w-3" /> Back to sign in
                  </button>
                </div>
                <p className="mt-3 text-xs text-muted-foreground">Locked out? Ask your practice admin to reset your two-factor authentication.</p>
              </CardContent>
            </Card>
            ) : (
            <Card className="shadow-lg shadow-black/5">
              <CardHeader>
                <CardTitle className="text-xl">Sign in</CardTitle>
                <CardDescription>Access your practice workspace.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={submit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="login-email" className="text-xs font-medium">Work email</Label>
                    <Input id="login-email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourpractice.com" data-testid="login-email-input" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password" className="text-xs font-medium">Password</Label>
                      <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
                    </div>
                    <Input id="login-password" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password-input" />
                  </div>
                  <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button">
                    {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Sign in
                  </Button>
                </form>
                <p className="mt-4 text-sm text-muted-foreground">
                  New practice?{' '}
                  <Link to="/signup" className="text-primary font-medium hover:underline" data-testid="login-signup-link">Create your workspace</Link>
                </p>
              </CardContent>
            </Card>
            )}

            {demoMode && (
            <Card className="bg-card/80 backdrop-blur" data-testid="login-demo-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Explore the interactive demo</CardTitle>
                <CardDescription className="text-xs">One click — no account needed. Uses the seeded Sunrise Orthopedic sandbox.</CardDescription>
              </CardHeader>
              <CardContent className="pt-1">
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" onClick={() => tryDemo('admin')} disabled={!!demoBusy} data-testid="login-demo-admin">
                    {demoBusy === 'admin' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1 text-primary" />} Admin
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => tryDemo('doctor')} disabled={!!demoBusy} data-testid="login-demo-doctor">
                    {demoBusy === 'doctor' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Stethoscope className="h-3.5 w-3.5 mr-1 text-primary" />} Doctor
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => tryDemo('nurse')} disabled={!!demoBusy} data-testid="login-demo-nurse">
                    {demoBusy === 'nurse' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ClipboardList className="h-3.5 w-3.5 mr-1 text-primary" />} Medical staff
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => tryDemo('owner')} disabled={!!demoBusy} data-testid="login-demo-owner">
                    {demoBusy === 'owner' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crown className="h-3.5 w-3.5 mr-1 text-primary" />} Owner
                  </Button>
                </div>
                {/* The plain-text demo credentials that used to sit here were a
                    published bypass of the access-code gate (GAPS S23) — and
                    stale the moment DEMO_PASSWORD is set. Prospects use the
                    buttons above; staff use their own account. */}
              </CardContent>
            </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
